'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useTranslation } from 'react-i18next';

// UI lib dependencies
import { Text, Group } from '@mantine/core';

// Assets
import {
  IconLock as IconPermissionAccessRedacted,
  IconEye as IconPermissionAccessOriginal,
  IconCircleOff as IconPermissionNone,
  IconProps,
} from '@tabler/icons-react';

// Types
import { ResourceStorageVariantEnum } from '@app-types/blackroom-app';
import { LabelStorageVariantProps } from './types';

// Style
import { FONT_WEIGHT } from '@ui-kit/fonts';
import { ICON_SIZE_MAP } from '@ui-kit/icons-sizes';
import { ForwardRefExoticComponent, RefAttributes } from 'react';

// Constants
const VariantIconMap: Record<
  ResourceStorageVariantEnum,
  ForwardRefExoticComponent<IconProps & RefAttributes<SVGSVGElement>>
> = {
  [ResourceStorageVariantEnum.redacted]: IconPermissionAccessRedacted,
  [ResourceStorageVariantEnum.original]: IconPermissionAccessOriginal,
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function LabelStorageVariant({
  storageVariant,
  display = 'icon-name',
  iconSize,
  labelProps,
  ...props
}: LabelStorageVariantProps) {
  /* ********************************** HOOKS ********************************* */

  // Localization
  const { t } = useTranslation('Permission');

  // Styles

  /* ***************************** LOCAL VARIABLES **************************** */

  const VariantIcon = VariantIconMap[storageVariant] ?? IconPermissionNone;

  const variantLabel = storageVariant
    ? t(`Common.storageVariant.${storageVariant}`)
    : t('Common.permission.ResourceNone');

  /* ******************************** RENDERING ******************************* */
  return display === 'icon-name' ? (
    <Group pl={0} gap="xs" wrap="nowrap" {...props}>
      <VariantIcon size={iconSize ?? ICON_SIZE_MAP.smaller} />
      <Text
        tt="none"
        fw={FONT_WEIGHT.normal}
        size="sm"
        style={{ textWrap: 'nowrap' }}
        {...labelProps}
      >
        {variantLabel}
      </Text>
    </Group>
  ) : (
    <VariantIcon size={ICON_SIZE_MAP.smaller} />
  );
}

export default LabelStorageVariant;
